/**
 * Configuration Template Types (schema v2)
 *
 * A template is a reusable configuration for ONE gateway type
 * (`device_type`: PGA, G2, P4GU…), owned by ONE client — or by ANEP, in
 * which case `client_id` is the global sentinel and every client may use
 * it. A device takes at most one template; there is no priority and no
 * stacking any more: gateway defaults → the device's template → the
 * device's own overrides.
 */

/** Owner value of an ANEP (global) template on the wire. */
export const ANEP_CLIENT_ID = '00000000-0000-0000-0000-000000000000';

export const isAnepTemplate = (clientId?: string | null): boolean =>
  !clientId || clientId.replace(/-/g, '') === ANEP_CLIENT_ID.replace(/-/g, '');

/**
 * Hardware type code for templates - fetched from /v2/hardware-types API.
 * Examples: 'PGA', 'IOTL', 'P4GU', 'TMS2', 'UNKN'.
 */
export type DeviceType = string;

/**
 * Custom field for device-specific configuration.
 * Name and value are added to effective_config.
 * Group and notes are metadata for organization.
 */
export interface CustomField {
  name: string;
  value: string | number | boolean;
  group?: string;
  notes?: string;
  source?: 'device_type' | 'hardware_field_def' | 'template' | 'device';
  default?: string | number | boolean;
  type?: 'string' | 'number' | 'boolean';
}

/**
 * Custom field definition from a device type's default_settings.
 * Defines the schema (name, type, default) for custom fields.
 */
export interface CustomFieldDefinition {
  name: string;
  type: 'string' | 'number' | 'boolean';
  default: string | number | boolean;
  group?: string;
  notes?: string;
}

/**
 * JSON configuration structure for device templates.
 * Contains all device-type specific configuration fields.
 */
export interface JsonConfig {
  // Syslog configuration
  syslog_host?: string;
  syslog_port?: number;
  syslog_facility?: number;

  // MQTT configuration
  mqtt_host?: string;
  mqtt_port?: number;
  mqtt_user?: string;
  mqtt_pass?: string;

  // Provisioning configuration
  provisioning?: string;
  keepalive_timer?: number;

  // Device password configuration
  password?: string;
  generate_password?: boolean;

  // Device-specific custom fields
  custom_fields?: CustomField[];

  // Allow additional device-type specific fields
  [key: string]: string | number | boolean | CustomField[] | undefined;
}

/**
 * Full configuration template with all fields.
 * Used in detail/edit views where complete data is needed.
 */
export interface ConfigTemplate {
  id: string;
  name: string;
  description?: string;
  /** Owner: a client UUID, or ANEP_CLIENT_ID for an ANEP template. */
  client_id: string;
  device_type: DeviceType;
  /** The default template for this owner and gateway type (at most one). */
  is_default: boolean;
  /** Devices currently using this template. */
  device_count: number;
  json_config: JsonConfig;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Configuration template list item (excludes json_config for performance).
 * Used in table/list views.
 */
export interface ConfigTemplateListItem {
  id: string;
  name: string;
  description?: string;
  client_id: string;
  device_type: DeviceType;
  is_default: boolean;
  /** Gateways following this template, within the caller's own clients. */
  device_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  /** The owner's name, resolved by the API (an agency may not list its root). */
  client_name?: string | null;
  /** Owned by the caller's root client, read through the agency (docs/agences.md). */
  inherited?: boolean;
}

/**
 * Request model for creating a new configuration template.
 */
export interface ConfigTemplateCreate {
  name: string;
  description?: string;
  /** Owner: a client UUID, or ANEP_CLIENT_ID (ANEP staff only). */
  client_id: string;
  device_type: DeviceType;
  is_default?: boolean;
  json_config: JsonConfig;
}

/**
 * Request model for updating an existing configuration template.
 */
export interface ConfigTemplateUpdate {
  name?: string;
  description?: string;
  client_id?: string;
  device_type?: DeviceType;
  is_default?: boolean;
  json_config?: JsonConfig;
  is_active?: boolean;
}

/**
 * Body of POST /v2/config-templates/{id}/assign (SuperAdmin).
 */
export interface ConfigTemplateAssignRequest {
  client_ids?: string[];
  device_ids?: string[];
  /** With device_ids: the client the devices must belong to. Foreign ones are skipped and listed. */
  expect_client_id?: string;
  /** Devices already linked to another template: re-link them (true) or leave them (false). */
  replace_existing_template: boolean;
  /** Drop the devices' own overrides so the template applies in full. */
  clear_overrides: boolean;
  /** Check and report only: nothing is written. */
  dry_run?: boolean;
}

export interface ConfigTemplateAssignResult {
  template_id: number;
  template_name: string;
  device_type: string;
  dry_run: boolean;
  matched: number;
  assigned: number;
  already_assigned: number;
  skipped_has_template: number;
  skipped_wrong_type: number;
  skipped_other_client: number;
  skipped_inactive: number;
  unknown_devices: string[];
  other_client_devices: string[];
  wrong_type_devices: string[];
  overrides_cleared: number;
}

/**
 * Configuration template statistics.
 */
export interface ConfigTemplateStats {
  total_templates: number;
  active_templates: number;
  templates_by_type: Record<string, number>;
  total_devices?: number;
  devices_by_type?: Record<string, number>;
}
